import { useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { challengesAPI, submissionsAPI, usersAPI } from '../../services/api'
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  TrophyIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
  FireIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import ImageWithFallback from '../../components/ui/ImageWithFallback'
import AdminAnalytics from '../../components/admin/AdminAnalytics'
import AdminDashboardDemo from '../../components/admin/AdminDashboardDemo'
import { getCategoryGradient, getPlaceholderImage, heroImages } from '../../utils/images'
import { format } from 'date-fns'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'text-yellow-600 bg-yellow-100',
  under_review: 'text-blue-600 bg-blue-100',
  evaluated: 'text-green-600 bg-green-100',
}

export default function AdminDashboard() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')

  // Admin stats
  const { data: adminStats, isLoading: statsLoading } = useQuery(
    'admin-stats',
    async () => {
      const [challengesRes, submissionsRes, usersRes] = await Promise.all([
        challengesAPI.getAll(),
        submissionsAPI.getMySubmissions({ limit: 1000 }), // Get all for admin
        usersAPI.getAll()
      ])
      
      const challenges = challengesRes.data.challenges
      const submissions = submissionsRes.data.submissions
      const users = usersRes.data.users || []

      return {
        totalChallenges: challenges.length,
        activeChallenges: challenges.filter(c => c.status === 'active').length,
        totalSubmissions: submissions.length,
        pendingSubmissions: submissions.filter(s => s.status === 'submitted').length,
        totalUsers: users.length,
        recentUsers: users.slice(-5)
      }
    }
  )

  // Recent challenges
  const { data: recentChallenges, isLoading: challengesLoading } = useQuery(
    'admin-recent-challenges',
    () => challengesAPI.getAll(),
    {
      select: (response) => response.data.challenges.slice(0, 5)
    }
  )

  // Pending submissions
  const { data: pendingSubmissions, isLoading: submissionsLoading } = useQuery(
    'admin-pending-submissions',
    () => submissionsAPI.getMySubmissions({ limit: 1000 }),
    {
      select: (response) => response.data.submissions.filter(s => s.status === 'submitted').slice(0, 10)
    }
  )

  if (statsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'challenges', name: 'Challenges', icon: TrophyIcon },
    { id: 'submissions', name: 'Submissions', icon: DocumentTextIcon },
    { id: 'users', name: 'Users', icon: UserGroupIcon },
  ]

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-600 overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={heroImages.dashboard}
            alt="Admin dashboard background"
            className="w-full h-full object-cover opacity-20"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300'%3E%3Crect width='1200' height='300' fill='%237c3aed'/%3E%3C/svg%3E"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center mb-4">
                <div className="h-16 w-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  <ChartBarIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h1 className="text-3xl font-bold text-white sm:text-4xl">
                    Admin Dashboard
                  </h1>
                  <p className="mt-2 text-lg text-purple-100">
                    Manage challenges, evaluate submissions, and oversee the platform
                  </p>
                </div>
              </div>
              {adminStats && (
                <div className="flex flex-wrap gap-6 mt-6">
                  <div className="flex items-center text-white">
                    <TrophyIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">{adminStats.totalChallenges} Total Challenges</span>
                  </div>
                  <div className="flex items-center text-white">
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">{adminStats.totalSubmissions} Submissions</span>
                  </div>
                  <div className="flex items-center text-white">
                    <UserGroupIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">{adminStats.totalUsers} Users</span>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
              <Link
                to="/admin/challenges/create"
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-purple-600 bg-white hover:bg-purple-50 transition-colors duration-200"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Create Challenge
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <AdminAnalytics />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Recent Challenges */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Recent Challenges
                    </h3>
                    <Link
                      to="/admin/challenges"
                      className="text-sm text-purple-600 hover:text-purple-500"
                    >
                      View all
                    </Link>
                  </div>
                  
                  {challengesLoading ? (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner />
                    </div>
                  ) : recentChallenges?.length > 0 ? (
                    <div className="space-y-3">
                      {recentChallenges.map((challenge) => (
                        <div key={challenge.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              challenge.status === 'active' ? 'bg-green-400' :
                              challenge.status === 'upcoming' ? 'bg-yellow-400' : 'bg-gray-400'
                            }`} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {challenge.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {challenge.category} • {challenge.difficulty}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/admin/challenges/${challenge.id}`}
                              className="text-purple-600 hover:text-purple-500"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                            <Link
                              to={`/admin/challenges/${challenge.id}/edit`}
                              className="text-blue-600 hover:text-blue-500"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No challenges yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Get started by creating your first challenge.
                      </p>
                      <div className="mt-6">
                        <Link
                          to="/admin/challenges/create"
                          className="btn-primary"
                        >
                          Create Challenge
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pending Submissions */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Pending Submissions
                    </h3>
                    <Link
                      to="/admin/submissions"
                      className="text-sm text-purple-600 hover:text-purple-500"
                    >
                      View all
                    </Link>
                  </div>
                  
                  {submissionsLoading ? (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner />
                    </div>
                  ) : pendingSubmissions?.length > 0 ? (
                    <div className="space-y-3">
                      {pendingSubmissions.map((submission) => {
                        const StatusIcon = statusIcons[submission.status]
                        return (
                          <div key={submission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className={`p-1 rounded-full ${statusColors[submission.status]}`}>
                                <StatusIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {submission.title}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {submission.challenge_title} • {submission.user_name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/admin/submissions/${submission.id}`}
                                className="text-purple-600 hover:text-purple-500"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No pending submissions</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        All submissions have been reviewed.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'challenges' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Challenge Management</h2>
              <Link
                to="/admin/challenges/create"
                className="btn-primary flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create New Challenge
              </Link>
            </div>
            <p className="text-gray-600 mb-4">
              Manage all challenges, create new ones, and monitor their progress.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/challenges"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <TrophyIcon className="h-8 w-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">All Challenges</h3>
                <p className="text-sm text-gray-500">View and manage all challenges</p>
              </Link>
              <Link
                to="/admin/challenges/create"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <PlusIcon className="h-8 w-8 text-green-600 mb-2" />
                <h3 className="font-medium text-gray-900">Create Challenge</h3>
                <p className="text-sm text-gray-500">Add a new challenge to the platform</p>
              </Link>
              <Link
                to="/admin/challenges/analytics"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <ChartBarIcon className="h-8 w-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">Analytics</h3>
                <p className="text-sm text-gray-500">View challenge performance metrics</p>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'submissions' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Submission Management</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Review, evaluate, and manage all submissions across all challenges.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/submissions"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <DocumentTextIcon className="h-8 w-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">All Submissions</h3>
                <p className="text-sm text-gray-500">View and manage all submissions</p>
              </Link>
              <Link
                to="/admin/submissions?status=submitted"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <ClockIcon className="h-8 w-8 text-yellow-600 mb-2" />
                <h3 className="font-medium text-gray-900">Pending Reviews</h3>
                <p className="text-sm text-gray-500">Submissions awaiting evaluation</p>
              </Link>
              <Link
                to="/admin/submissions/analytics"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <ChartBarIcon className="h-8 w-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">Evaluation Analytics</h3>
                <p className="text-sm text-gray-500">View submission and scoring metrics</p>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Manage users, view their activity, and handle user-related tasks.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/users"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <UserGroupIcon className="h-8 w-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">All Users</h3>
                <p className="text-sm text-gray-500">View and manage all users</p>
              </Link>
              <Link
                to="/admin/users/analytics"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <ChartBarIcon className="h-8 w-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">User Analytics</h3>
                <p className="text-sm text-gray-500">View user engagement metrics</p>
              </Link>
              <Link
                to="/admin/users/roles"
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
              >
                <StarIcon className="h-8 w-8 text-green-600 mb-2" />
                <h3 className="font-medium text-gray-900">Role Management</h3>
                <p className="text-sm text-gray-500">Manage user roles and permissions</p>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}